import React from 'react';

const RELOAD_MARK_KEY = 'moduleChunkReloadedAt';

/**
 * How long a reload counts as "we already tried this".
 *
 * Short on purpose: a stale-deploy failure is fixed by the very next load, so
 * a second failure arriving within seconds means reloading is not the answer.
 * Anything later is treated as a fresh problem worth one more reload.
 */
const RELOAD_WINDOW_MS = 10000;

function isChunkLoadError (error) {
    if (!error) {
        return false;
    }

    return error.name === 'ChunkLoadError' ||
        /Loading chunk .* failed/i.test(error.message || '');
}

/**
 * Reading storage can throw (private mode, blocked cookies). If we cannot tell
 * whether we already reloaded, we answer `true` so the reload is skipped:
 * showing the error screen once is far better than an endless reload loop.
 */
function reloadedRecently () {
    try {
        const reloadedAt = Number(window.sessionStorage.getItem(RELOAD_MARK_KEY));

        return Boolean(reloadedAt) && Date.now() - reloadedAt < RELOAD_WINDOW_MS;
    }
    catch (e) {
        return true;
    }
}

function markReloaded () {
    try {
        window.sessionStorage.setItem(RELOAD_MARK_KEY, String(Date.now()));

        return true;
    }
    catch (e) {
        return false;
    }
}

/**
 * Catches failures raised while a lazily loaded module is being downloaded.
 *
 * Code splitting turns navigating to a section into a network request. If that
 * request fails the lazy component throws, React unmounts the tree and the
 * user is left with a blank screen and no way back other than a refresh.
 * This boundary turns that into the `fallback` given by the caller.
 *
 * It has to be a class component: React offers no hook equivalent to
 * `componentDidCatch`.
 *
 * ---
 *
 * IMPORTANT: this boundary has a deliberate SIDE EFFECT - it can reload the page.
 *
 * The most common chunk failure in production is not a broken connection, it
 * is a deploy happening while somebody has the app open. Their page still
 * points at the previous build's file names, which no longer exist, so the
 * first section they open that was not already downloaded answers 404.
 *
 * A reload fixes that completely: it fetches a fresh `index.html` with the new
 * names. So on a chunk error we reload once, silently, and the user never
 * finds out. Only if it fails AGAIN within `RELOAD_WINDOW_MS` do we show the
 * error screen - at that point reloading clearly is not solving anything, and
 * insisting would trap the user in a reload loop.
 */
export class ModuleErrorBoundary extends React.Component {
    constructor (props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError () {
        return { hasError: true };
    }

    componentDidCatch (error) {
        if (isChunkLoadError(error) && !reloadedRecently() && markReloaded()) {
            window.location.reload();
        }
    }

    render () {
        if (this.state.hasError) {
            return this.props.fallback;
        }

        return this.props.children;
    }
}
